// User-facing labels for the agent's "allowed data scope" chips. The backend
// stores these as table names on `agents.allowed_tables`; we never want to
// surface raw identifiers like `bank_statement` or `accounting_entries` in the
// UI. Keep this list in sync with KNOWN_TABLES in
// `frontend/backend/tenant/services/agent-service.js`.

export const DATA_SCOPE_LABELS: Record<string, string> = {
  // Banking
  bank_statement: 'Bank transactions',
  plaid_items: 'Bank connections',
  // Ledger & rules
  accounting_entries: 'Ledger entries',
  journal_entries: 'Journal entries',
  categorization_rules: 'Categorization rules',
  budgets: 'Budgets',
  // Period adjustments
  accruals: 'Accruals',
  allocation_schedules: 'Allocation schedules',
  scheduled_transactions: 'Scheduled transactions',
  // Accounts receivable
  invoices: 'Invoices',
  customers: 'Customers',
  customer_payments: 'Customer payments',
  invoice_reminders: 'Invoice reminders',
  invoice_status_history: 'Invoice status history',
  credit_notes: 'Credit notes',
  refunds: 'Refunds',
  invoice_write_offs: 'Invoice write-offs',
  // Payouts (Bokapsys-managed mode)
  tenant_payouts: 'Payouts',
  tenant_payout_items: 'Payout line items',
  tenant_bank_accounts: 'Bank accounts',
  // Accounts payable
  bills: 'Bills',
  vendors: 'Vendors',
  vendor_payments: 'Vendor payments',
  bill_status_history: 'Bill status history',
  vendor_bank_accounts: 'Vendor bank accounts',
  // AP intelligence
  bill_extraction_results: 'Bill extraction results',
  duplicate_detection_results: 'Duplicate detection',
  ap_exceptions: 'AP exceptions',
  // Payment orchestration
  payment_batches: 'Payment batches',
  payment_items: 'Payment items',
  payment_events: 'Payment events',
  // Risk
  anomaly_flags: 'Anomaly flags',
  // Recurring AR
  recurring_invoice_templates: 'Recurring invoices',
  // Approvals
  approval_workflow_instances: 'Approval requests',
  approval_policies: 'Approval policies',
  // Operational surfaces
  notifications: 'Notifications',
  audit_logs: 'Audit log',
}

export type DataScopeGroup = { label: string; tables: string[] }

export const DATA_SCOPE_GROUPS: DataScopeGroup[] = [
  { label: 'Banking', tables: ['bank_statement', 'plaid_items'] },
  {
    label: 'Ledger & rules',
    tables: ['accounting_entries', 'journal_entries', 'categorization_rules', 'budgets'],
  },
  {
    label: 'Period adjustments',
    tables: ['accruals', 'allocation_schedules', 'scheduled_transactions'],
  },
  {
    label: 'Accounts receivable',
    tables: [
      'invoices',
      'customers',
      'customer_payments',
      'invoice_reminders',
      'invoice_status_history',
      'credit_notes',
      'refunds',
      'invoice_write_offs',
    ],
  },
  {
    label: 'Payouts',
    tables: [
      'tenant_payouts',
      'tenant_payout_items',
      'tenant_bank_accounts',
    ],
  },
  {
    label: 'Accounts payable',
    tables: [
      'bills',
      'vendors',
      'vendor_payments',
      'bill_status_history',
      'vendor_bank_accounts',
    ],
  },
  {
    label: 'AP intelligence',
    tables: ['bill_extraction_results', 'duplicate_detection_results', 'ap_exceptions'],
  },
  {
    label: 'Payment orchestration',
    tables: ['payment_batches', 'payment_items', 'payment_events'],
  },
  { label: 'Risk', tables: ['anomaly_flags'] },
  {
    label: 'Recurring AR',
    tables: ['recurring_invoice_templates'],
  },
  {
    label: 'Approvals',
    tables: ['approval_workflow_instances', 'approval_policies'],
  },
  {
    label: 'Operational',
    tables: ['notifications', 'audit_logs'],
  },
]

export function dataScopeLabel(table: string): string {
  return DATA_SCOPE_LABELS[table] ?? table.replace(/_/g, ' ')
}
